
import React, { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { getStorageUrl, getWelcomeIconUrl, getCommunityIconUrl, type StorageBucket, APP_IMAGES } from '@/utils/storage';

interface ImageManagerProps {
  src?: string;
  alt: string;
  type: 'avatar' | 'hero' | 'attachment' | 'prayer-card' | 'welcome-icon' | 'community-icon';
  className?: string;
  fallbackSrc?: string;
  children?: React.ReactNode;
  width?: number;
  height?: number;
  // New props for Supabase storage integration
  storageBucket?: StorageBucket;
  storagePath?: string;
  // Theme prop for welcome icons
  theme?: 'light' | 'dark';
}

const DEFAULT_IMAGES = {
  avatar: APP_IMAGES.abielAvatar, // Use Abiel avatar as default
  hero: APP_IMAGES.prayerFlame,
  attachment: 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=400&h=300&fit=crop',
  'prayer-card': 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=300&h=200&fit=crop',
  'welcome-icon': APP_IMAGES.welcomeIconLight, // Default to light theme
  'community-icon': APP_IMAGES.communityIconLight // Default to light theme
};

export default function ImageManager({ 
  src, 
  alt, 
  type, 
  className = '', 
  fallbackSrc,
  children,
  width,
  height,
  storageBucket,
  storagePath,
  theme = 'light'
}: ImageManagerProps) {
  const [currentSrc, setCurrentSrc] = useState(() => {
    // Priority: explicit src > storage path > theme-based icons > default
    if (src) return src;
    if (storageBucket && storagePath) return getStorageUrl(storageBucket, storagePath);
    if (type === 'welcome-icon') return getWelcomeIconUrl(theme);
    if (type === 'community-icon') return getCommunityIconUrl(theme);
    return DEFAULT_IMAGES[type];
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Update source when props change
    if (src) {
      setCurrentSrc(src);
    } else if (storageBucket && storagePath) {
      setCurrentSrc(getStorageUrl(storageBucket, storagePath));
    } else if (type === 'welcome-icon') {
      setCurrentSrc(getWelcomeIconUrl(theme));
    } else if (type === 'community-icon') {
      setCurrentSrc(getCommunityIconUrl(theme));
    } else {
      setCurrentSrc(DEFAULT_IMAGES[type]);
    }
    setHasError(false);
    setIsLoading(true);
  }, [src, type, storageBucket, storagePath, theme]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    
    // Try fallback source first, then default
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
    } else if (currentSrc !== DEFAULT_IMAGES[type]) {
      setCurrentSrc(DEFAULT_IMAGES[type]);
    }
  };

  // For avatar type, use the Avatar component
  if (type === 'avatar') {
    return (
      <Avatar className={className}>
        <AvatarImage 
          src={currentSrc} 
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
        />
        <AvatarFallback>
          {/* Use Abiel avatar as fallback instead of text initials */}
          <img 
            src={APP_IMAGES.abielAvatar} 
            alt="Abiel Avatar" 
            className="w-full h-full object-cover"
          />
        </AvatarFallback>
      </Avatar>
    );
  }

  // For other image types
  return (
    <div className={`relative ${className}`}>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse rounded" />
      )}
      
      <img
        src={currentSrc}
        alt={alt}
        width={width}
        height={height}
        className={`w-full h-full object-cover transition-opacity duration-300 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={handleLoad}
        onError={handleError}
      />
      
      {hasError && !isLoading && (
        <div className="absolute inset-0 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          <span className="text-gray-500 dark:text-gray-400 text-sm">
            Failed to load image
          </span>
        </div>
      )}
      
      {children}
    </div>
  );
}
